import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { MapPin, Briefcase, GraduationCap, Heart, MessageSquare, Phone, Mail, User, Users as UsersIcon, Home, ArrowLeft, Check, Lock, Clock, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import { premiumAPI, profileAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';
import VerifiedBadge from '../components/VerifiedBadge';
import axios from 'axios';
import { COUNTRIES } from '../constants/countries';
import useWebSocket from '../hooks/useWebSocket';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Helper function to format location display
const getLocationDisplay = (profile) => {
  if (!profile) return null;
  
  // Priority 1: Use country and province fields (new format)
  if (profile.country && profile.province) {
    const countryName = COUNTRIES.find(c => c.code === profile.country)?.name || profile.country;
    return `${profile.province}, ${countryName}`;
  }
  
  // Priority 2: Extract province from location field (legacy format)
  // Location format is "City, Province" - we want to show "Province, Country"
  if (profile.location) {
    const parts = profile.location.split(',');
    if (parts.length >= 2) {
      // Second part is province, hide city for privacy
      const province = parts[1].trim();
      
      // Try to get country from address object
      if (profile.address && profile.address.country) {
        const countryName = COUNTRIES.find(c => c.code === profile.address.country)?.name || profile.address.country;
        return `${province}, ${countryName}`;
      }
      
      // If no country in address, just show province
      return province;
    }
    
    // If location doesn't have comma, show as-is
    return profile.location;
  }
  
  return null;
};

const ProfileDetailPage = () => {
  const { id } = useParams();
  const location = useLocation();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const { isAuthenticated, user } = useAuth();
  const [premiumStatus, setPremiumStatus] = useState(null);
  const [interestStatus, setInterestStatus] = useState(null);
  const [sendingInterest, setSendingInterest] = useState(false);
  const [isShortlisted, setIsShortlisted] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();
  
  // WebSocket for real-time updates
  const { lastMessage } = useWebSocket(user?.id);
  
  // Get return URL from location state (from search page or messages)
  const returnUrl = location.state?.returnUrl || '/search';

  // Listen for WebSocket messages (interest accepted)
  useEffect(() => {
    if (lastMessage) {
      if (lastMessage.type === 'interest_accepted' && lastMessage.receiverId === id) {
        // The user we're viewing accepted our interest - update UI
        toast({
          title: "Interest Accepted! 🎉",
          description: `${lastMessage.receiverName} accepted your interest! You can now message them.`,
        });
        // Refresh interest status to show "Send Message" button
        checkInterestStatus();
      }
    }
  }, [lastMessage, id, toast]);

  useEffect(() => {
    if (isAuthenticated) {
      loadPremiumStatus();
    }
  }, [isAuthenticated, id]);

  useEffect(() => {
    if (isAuthenticated && premiumStatus !== null) {
      // Check if user is premium before fetching profile
      if (!premiumStatus.isPremium) {
        toast({
          title: "Premium Required",
          description: "You need a premium subscription to view profiles",
          variant: "destructive",
        });
        navigate('/pricing');
        return;
      }
      fetchProfile();
      checkInterestStatus();
    }
  }, [isAuthenticated, id, premiumStatus]);

  const checkInterestStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/interests/status/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` },
        withCredentials: true
      });
      setInterestStatus(response.data);
    } catch (error) {
      console.error('Failed to check interest status:', error);
    }
  };

  const handleSendInterest = async () => {
    try {
      setSendingInterest(true);
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/interests/send?receiver_id=${id}`, {}, {
        headers: { 'Authorization': `Bearer ${token}` },
        withCredentials: true
      });

      toast({
        title: "Interest Sent!",
        description: "Your interest has been sent successfully",
      });

      checkInterestStatus();
    } catch (error) {
      console.error('Failed to send interest:', error);
      
      // Check if it's a premium required error
      if (error.response?.status === 403 && error.response?.data?.detail?.includes('Premium')) {
        toast({
          title: "Premium Required",
          description: "Upgrade to premium to send interests",
          variant: "destructive",
        });
        navigate('/pricing');
      } else {
        toast({
          title: "Error",
          description: error.response?.data?.detail || "Failed to send interest",
          variant: "destructive",
        });
      }
    } finally {
      setSendingInterest(false);
    }
  };

  const handleToggleShortlist = async () => {
    try {
      if (isShortlisted) {
        await profileAPI.removeFromShortlist(id);
        setIsShortlisted(false);
        toast({
          title: "Removed from Shortlist",
          description: "Profile has been removed from your shortlist",
        });
      } else {
        await profileAPI.addToShortlist(id);
        setIsShortlisted(true);
        toast({
          title: "Added to Shortlist",
          description: "Profile has been added to your shortlist",
        });
      }
    } catch (error) {
      console.error('Error updating shortlist:', error);
      toast({
        title: "Error",
        description: "Failed to update shortlist. Please try again.",
        variant: "destructive",
      });
    }
  };

  const fetchProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/user/profile/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        withCredentials: true
      });
      setProfile(response.data.user);
      
      // Check if profile is in shortlist
      const shortlistData = await profileAPI.getShortlist();
      const isInShortlist = shortlistData.shortlist?.some(p => p.id === id);
      setIsShortlisted(isInShortlist);
    } catch (error) {
      console.error('Failed to fetch profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPremiumStatus = async () => {
    try {
      const data = await premiumAPI.getStatus();
      setPremiumStatus(data);
    } catch (error) {
      console.error('Failed to load premium status:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-mint-500"></div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Profile not found</h2>
          <Link to="/search">
            <Button>Back to Search</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="w-full px-2 py-8">
        {/* Back Button */}
        <button 
          onClick={() => navigate(returnUrl)}
          className="inline-flex items-center text-gray-600 hover:text-navy-500 mb-6"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Images and Actions */}
          <div className="lg:col-span-1">
            <Card>
              <CardContent className="p-0">
                <div className="relative">
                  <img
                    src={profile.image}
                    alt={`${profile.firstName} ${profile.lastName}`}
                    className={`w-full h-80 object-cover rounded-t-lg ${
                      !premiumStatus?.isPremium ? 'blur-md' : ''
                    }`}
                  />
                  {!premiumStatus?.isPremium && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/30 rounded-t-lg">
                      <div className="text-center text-white">
                        <Phone className="w-12 h-12 mx-auto mb-2" />
                        <p className="font-semibold text-lg">Subscribe to View</p>
                        <Link to="/">
                          <Button className="mt-3 bg-mint-500 hover:bg-mint-600">
                            Get Premium
                          </Button>
                        </Link>
                      </div>
                    </div>
                  )}
                  {profile.isPremium && (
                    <Badge className="absolute top-3 right-3 bg-yellow-500 text-white">
                      Premium Member
                    </Badge>
                  )}
                </div>
                <div className="p-6 space-y-3">
                  {/* Send Interest Button */}
                  {!interestStatus?.sentStatus && !interestStatus?.receivedStatus && (
                    <Button 
                      className="w-full bg-coral-500 hover:bg-coral-600 text-white"
                      onClick={() => {
                        if (premiumStatus?.isPremium) {
                          handleSendInterest();
                        } else {
                          toast({
                            title: "Premium Required",
                            description: "Upgrade to premium to send interests",
                          });
                          navigate('/pricing');
                        }
                      }}
                      disabled={sendingInterest}
                    >
                      <Heart className="w-4 h-4 mr-2" />
                      {sendingInterest ? 'Sending...' : 'Send Interest'}
                    </Button>
                  )}
                  
                  {interestStatus?.sentStatus === 'pending' && (() => {
                    // Check if interest was sent more than 7 days ago
                    const sentDate = new Date(interestStatus.createdAt);
                    const daysSince = Math.floor((new Date() - sentDate) / (1000 * 60 * 60 * 24));
                    const canResend = daysSince >= 7;
                    
                    if (canResend) {
                      return (
                        <Button 
                          className="w-full bg-orange-500 hover:bg-orange-600 text-white"
                          onClick={handleSendInterest}
                          disabled={sendingInterest}
                        >
                          <Clock className="w-4 h-4 mr-2" />
                          {sendingInterest ? 'Resending...' : 'Resend Interest'}
                        </Button>
                      );
                    } else {
                      return (
                        <Button 
                          className="w-full bg-yellow-500 hover:bg-yellow-600 text-white"
                          disabled
                        >
                          <Clock className="w-4 h-4 mr-2" />
                          Interest Sent (Pending)
                        </Button>
                      );
                    }
                  })()}
                  
                  {(interestStatus?.sentStatus === 'accepted' || interestStatus?.receivedStatus === 'accepted') && (
                    <Button 
                      className="w-full bg-green-600 hover:bg-green-700 text-white"
                      onClick={() => {
                        if (premiumStatus?.isPremium) {
                          navigate(`/messages/${profile.id}`, { 
                            state: { returnUrl: `/profile/${profile.id}` } 
                          });
                        } else {
                          toast({
                            title: "Premium Required",
                            description: "Upgrade to premium to send messages",
                          });
                          navigate('/pricing');
                        }
                      }}
                    >
                      <MessageSquare className="w-4 h-4 mr-2" />
                      Send Message
                    </Button>
                  )}

                  {/* Send Message Button - Only show if can message and not already showing Send Message from accepted interest */}
                  {interestStatus?.canMessage && interestStatus?.sentStatus !== 'accepted' && interestStatus?.receivedStatus !== 'accepted' && (
                    <Button 
                      variant="outline" 
                      className="w-full"
                      onClick={() => {
                        if (premiumStatus?.isPremium) {
                          navigate(`/messages/${profile.id}`, { 
                            state: { returnUrl: `/profile/${profile.id}` } 
                          });
                        } else {
                          toast({
                            title: "Premium Required",
                            description: "Upgrade to premium to send messages",
                          });
                          navigate('/pricing');
                        }
                      }}
                    >
                      <MessageSquare className="w-4 h-4 mr-2" />
                      Send Message
                    </Button>
                  )}

                  <Button 
                    variant="outline" 
                    className={`w-full ${isShortlisted ? 'bg-cream-100 border-red-300 text-navy-500 hover:bg-red-100' : ''}`}
                    onClick={handleToggleShortlist}
                  >
                    <Heart className={`w-4 h-4 mr-2 ${isShortlisted ? 'fill-current' : ''}`} />
                    {isShortlisted ? 'Remove from Shortlist' : 'Add to Shortlist'}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Additional Photos */}
            {profile.images && profile.images.length > 1 && (
              <Card className="mt-6">
                <CardHeader>
                  <CardTitle className="text-lg">More Photos</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-2">
                    {profile.images.slice(1).map((img, index) => (
                      <div key={index} className="relative">
                        <img
                          src={img}
                          alt={`${profile.name} ${index + 2}`}
                          className={`w-full h-32 object-cover rounded-lg ${
                            !premiumStatus?.isPremium ? 'blur-md' : ''
                          }`}
                        />
                        {!premiumStatus?.isPremium && (
                          <div className="absolute inset-0 flex items-center justify-center bg-black/30 rounded-lg">
                            <Phone className="w-6 h-6 text-white" />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right Column - Profile Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Name and Basic Info Card */}
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-2">
                  <h1 className="text-3xl font-bold text-gray-900">
                    {profile.firstName} {profile.lastName}
                  </h1>
                  {(profile.isVerified || profile.govIdVerified) && <VerifiedBadge size="large" showText={false} />}
                </div>
                <p className="text-gray-600">
                  {profile.age} years | {profile.height || 'Not specified'} | {profile.maritalStatus || 'Not specified'}
                </p>
                <p className="text-sm text-green-600 mt-1">
                  Last active: {profile.lastActive || 'Recently'}
                </p>
              </CardContent>
            </Card>

            {/* About Section */}
            {profile.about && (
              <Card>
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                    <User className="w-5 h-5 mr-2 text-navy-500" />
                    About
                  </h3>
                  <p className="text-gray-700 leading-relaxed">{profile.about}</p>
                </CardContent>
              </Card>
            )}

            {/* Personal Details */}
            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                  <User className="w-5 h-5 mr-2 text-navy-500" />
                  Personal Details
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="flex items-start">
                        <User className="w-5 h-5 text-gray-400 mr-3 mt-0.5" />
                        <div>
                          <p className="text-sm text-gray-600">Age</p>
                          <p className="font-medium">{profile.age} years</p>
                        </div>
                      </div>
                      {profile.height && (
                        <div className="flex items-start">
                          <User className="w-5 h-5 text-gray-400 mr-3 mt-0.5" />
                          <div>
                            <p className="text-sm text-gray-600">Height</p>
                            <p className="font-medium">{profile.height}</p>
                          </div>
                        </div>
                      )}
                      {profile.weight && (
                        <div className="flex items-start">
                          <User className="w-5 h-5 text-gray-400 mr-3 mt-0.5" />
                          <div>
                            <p className="text-sm text-gray-600">Weight</p>
                            <p className="font-medium">{profile.weight} kg</p>
                          </div>
                        </div>
                      )}
                      {profile.build && (
                        <div className="flex items-start">
                          <User className="w-5 h-5 text-gray-400 mr-3 mt-0.5" />
                          <div>
                            <p className="text-sm text-gray-600">Build</p>
                            <p className="font-medium">{profile.build}</p>
                          </div>
                        </div>
                      )}
                      {getLocationDisplay(profile) && (
                        <div className="flex items-start">
                          <MapPin className="w-5 h-5 text-gray-400 mr-3 mt-0.5" />
                          <div>
                            <p className="text-sm text-gray-600">Location</p>
                            <p className="font-medium">{getLocationDisplay(profile)}</p>
                          </div>
                        </div>
                      )}
                      {profile.religion && (
                        <div className="flex items-start">
                          <Heart className="w-5 h-5 text-coral-500 mr-3 mt-0.5" />
                          <div>
                            <p className="text-sm text-gray-600">Religion</p>
                            <p className="font-medium">{profile.religion}</p>
                          </div>
                        </div>
                      )}
                      {profile.motherTongue && (
                        <div className="flex items-start">
                          <Mail className="w-5 h-5 text-gray-400 mr-3 mt-0.5" />
                          <div>
                            <p className="text-sm text-gray-600">Language</p>
                            <p className="font-medium">{profile.motherTongue}</p>
                          </div>
                        </div>
                      )}
                      {profile.maritalStatus && (
                        <div className="flex items-start">
                          <Heart className="w-5 h-5 text-coral-500 mr-3 mt-0.5" />
                          <div>
                            <p className="text-sm text-gray-600">Marital Status</p>
                            <p className="font-medium">{profile.maritalStatus}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

            {/* Professional Details */}
            <Card>
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                    <Briefcase className="w-5 h-5 mr-2 text-navy-500" />
                    Professional Details
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {profile.education && (
                      <div className="flex items-start">
                        <GraduationCap className="w-5 h-5 text-gray-400 mr-3 mt-0.5" />
                        <div>
                          <p className="text-sm text-gray-600">Regular Education</p>
                          <p className="font-medium">{profile.education}</p>
                        </div>
                      </div>
                    )}
                    {profile.islamicEducation && (
                      <div className="flex items-start">
                        <GraduationCap className="w-5 h-5 text-gray-400 mr-3 mt-0.5" />
                        <div>
                          <p className="text-sm text-gray-600">Islamic Education</p>
                          <p className="font-medium">{profile.islamicEducation}</p>
                        </div>
                      </div>
                    )}
                    {profile.jobCategory && (
                      <div className="flex items-start">
                        <Briefcase className="w-5 h-5 text-gray-400 mr-3 mt-0.5" />
                        <div>
                          <p className="text-sm text-gray-600">Job Category</p>
                          <p className="font-medium">{profile.jobCategory}</p>
                        </div>
                      </div>
                    )}
                    {profile.occupation && (
                      <div className="flex items-start">
                        <Briefcase className="w-5 h-5 text-gray-400 mr-3 mt-0.5" />
                        <div>
                          <p className="text-sm text-gray-600">Job Title / Occupation</p>
                          <p className="font-medium">{profile.occupation}</p>
                        </div>
                      </div>
                    )}
                    {profile.income && (
                      <div className="flex items-start">
                        <Briefcase className="w-5 h-5 text-gray-400 mr-3 mt-0.5" />
                        <div>
                          <p className="text-sm text-gray-600">Income Range</p>
                          <p className="font-medium">{profile.income}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

            {/* Family Details */}
            {profile.familyDetails && (
              <Card>
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                    <UsersIcon className="w-5 h-5 mr-2 text-navy-500" />
                    Family Details
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {profile.familyDetails.familyStatus && (
                      <div className="flex items-start">
                        <Home className="w-5 h-5 text-gray-400 mr-3 mt-0.5" />
                        <div>
                          <p className="text-sm text-gray-600">Family Status</p>
                          <p className="font-medium">{profile.familyDetails.familyStatus}</p>
                        </div>
                      </div>
                    )}
                    {profile.familyDetails.familyType && (
                      <div className="flex items-start">
                        <UsersIcon className="w-5 h-5 text-gray-400 mr-3 mt-0.5" />
                        <div>
                          <p className="text-sm text-gray-600">Family Type</p>
                          <p className="font-medium">{profile.familyDetails.familyType}</p>
                        </div>
                      </div>
                    )}
                    {profile.familyDetails.fatherOccupation && (
                      <div className="flex items-start">
                        <Briefcase className="w-5 h-5 text-gray-400 mr-3 mt-0.5" />
                        <div>
                          <p className="text-sm text-gray-600">Father's Occupation</p>
                          <p className="font-medium">{profile.familyDetails.fatherOccupation}</p>
                        </div>
                      </div>
                    )}
                    {profile.familyDetails.motherOccupation && (
                      <div className="flex items-start">
                        <Briefcase className="w-5 h-5 text-gray-400 mr-3 mt-0.5" />
                        <div>
                          <p className="text-sm text-gray-600">Mother's Occupation</p>
                          <p className="font-medium">{profile.familyDetails.motherOccupation}</p>
                        </div>
                      </div>
                    )}
                    {profile.familyDetails.siblings && (
                      <div className="flex items-start">
                        <UsersIcon className="w-5 h-5 text-gray-400 mr-3 mt-0.5" />
                        <div>
                          <p className="text-sm text-gray-600">Siblings</p>
                          <p className="font-medium">{profile.familyDetails.siblings}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Religious Information */}
            {profile.religiousInfo && (
              <Card>
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                    <Star className="w-5 h-5 mr-2 text-navy-500" />
                    Religious Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {profile.religiousInfo.madhhab && (
                      <div className="flex items-start">
                        <Star className="w-5 h-5 mr-3 text-gray-400 mt-0.5" />
                        <div>
                          <p className="text-sm text-gray-600">Madhhab</p>
                          <p className="font-medium">{profile.religiousInfo.madhhab}</p>
                        </div>
                      </div>
                    )}
                    {profile.religiousInfo.prayerHabit && (
                      <div className="flex items-start">
                        <Star className="w-5 h-5 mr-3 text-gray-400 mt-0.5" />
                        <div>
                          <p className="text-sm text-gray-600">Prayer Habit</p>
                          <p className="font-medium">{profile.religiousInfo.prayerHabit}</p>
                        </div>
                      </div>
                    )}
                    {profile.religiousInfo.beard && (
                      <div className="flex items-start">
                        <Star className="w-5 h-5 mr-3 text-gray-400 mt-0.5" />
                        <div>
                          <p className="text-sm text-gray-600">Beard</p>
                          <p className="font-medium">{profile.religiousInfo.beard}</p>
                        </div>
                      </div>
                    )}
                    {profile.religiousInfo.hijabNiqab && (
                      <div className="flex items-start">
                        <Star className="w-5 h-5 mr-3 text-gray-400 mt-0.5" />
                        <div>
                          <p className="text-sm text-gray-600">Hijab/Niqab</p>
                          <p className="font-medium">{profile.religiousInfo.hijabNiqab}</p>
                        </div>
                      </div>
                    )}
                    {profile.religiousInfo.quranRecitation && (
                      <div className="flex items-start">
                        <Star className="w-5 h-5 mr-3 text-gray-400 mt-0.5" />
                        <div>
                          <p className="text-sm text-gray-600">Quran Recitation</p>
                          <p className="font-medium">{profile.religiousInfo.quranRecitation}</p>
                        </div>
                      </div>
                    )}
                    {profile.religiousInfo.religiousCommitment && (
                      <div className="flex items-start">
                        <Star className="w-5 h-5 mr-3 text-gray-400 mt-0.5" />
                        <div>
                          <p className="text-sm text-gray-600">Religious Commitment</p>
                          <p className="font-medium">{profile.religiousInfo.religiousCommitment}</p>
                        </div>
                      </div>
                    )}
                    {profile.religiousInfo.islamicPractices && (
                      <div className="flex items-start">
                        <Star className="w-5 h-5 mr-3 text-gray-400 mt-0.5" />
                        <div>
                          <p className="text-sm text-gray-600">Islamic Practices</p>
                          <p className="font-medium">{profile.religiousInfo.islamicPractices}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Partner Preferences */}
            {profile.partnerPreferences && (
              <Card>
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                    <Heart className="w-5 h-5 mr-2 text-navy-500" />
                    Partner Preferences
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {profile.partnerPreferences.ageRange && (
                      <div className="flex items-start">
                        <User className="w-5 h-5 text-gray-400 mr-3 mt-0.5" />
                        <div>
                          <p className="text-sm text-gray-600">Age Range</p>
                          <p className="font-medium">{profile.partnerPreferences.ageRange} years</p>
                        </div>
                      </div>
                    )}
                    {profile.partnerPreferences.heightRange && (
                      <div className="flex items-start">
                        <User className="w-5 h-5 text-gray-400 mr-3 mt-0.5" />
                        <div>
                          <p className="text-sm text-gray-600">Height Range</p>
                          <p className="font-medium">{profile.partnerPreferences.heightRange}</p>
                        </div>
                      </div>
                    )}
                    {profile.partnerPreferences.maritalStatus && (
                      <div className="flex items-start">
                        <Heart className="w-5 h-5 text-coral-500 mr-3 mt-0.5" />
                        <div>
                          <p className="text-sm text-gray-600">Marital Status</p>
                          <p className="font-medium">{profile.partnerPreferences.maritalStatus}</p>
                        </div>
                      </div>
                    )}
                    {profile.partnerPreferences.religion && (
                      <div className="flex items-start">
                        <Heart className="w-5 h-5 text-coral-500 mr-3 mt-0.5" />
                        <div>
                          <p className="text-sm text-gray-600">Religion</p>
                          <p className="font-medium">{profile.partnerPreferences.religion}</p>
                        </div>
                      </div>
                    )}
                    {profile.partnerPreferences.education && (
                      <div className="flex items-start">
                        <GraduationCap className="w-5 h-5 text-gray-400 mr-3 mt-0.5" />
                        <div>
                          <p className="text-sm text-gray-600">Education</p>
                          <p className="font-medium">{profile.partnerPreferences.education}</p>
                        </div>
                      </div>
                    )}
                    {profile.partnerPreferences.occupation && (
                      <div className="flex items-start">
                        <Briefcase className="w-5 h-5 text-gray-400 mr-3 mt-0.5" />
                        <div>
                          <p className="text-sm text-gray-600">Occupation</p>
                          <p className="font-medium">{profile.partnerPreferences.occupation}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileDetailPage;